import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';

import type { BaseCampaign } from '@feathr/blackbox';
import type { IColumn } from '@feathr/components';
import { TableColumnHeader, Time } from '@feathr/components';
import { CampaignState } from '@feathr/extender/components';
import { useLocalUrl } from '@feathr/extender/state';
import { TimeFormat } from '@feathr/hooks';

import type { TPartnerMessage } from '../partnerMessages.utils';
import { getPartnerMessageSendTime } from '../partnerMessages.utils';
import EventPartnersMessagesOptions from './EventPartnersMessagesOptions';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const eventPartnersMessagesColumns: Array<IColumn<BaseCampaign>> = [
  {
    id: 'name',
    Header: TableColumnHeader({
      sortType: 'numeric',
      title: 'Name',
    }),
    headerClassName: tableStyles.sort,
    className: tableStyles.cell,
    minWidth: 200,
    Cell({ original }): JSX.Element {
      return (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const localUrl = useLocalUrl();
            const name = original.get('name');
            return <Link to={localUrl(original.getItemUrl())}>{name || 'Unnamed message'}</Link>;
          }}
        </Observer>
      );
    },
  },
  {
    id: 'state',
    Header: TableColumnHeader({
      sortType: 'alpha',
      title: 'Status',
    }),
    headerClassName: tableStyles.sort,
    Cell({ original }): JSX.Element {
      const state = original.get('state');
      return <CampaignState state={state} />;
    },
  },
  {
    id: 'date_created',
    Header: TableColumnHeader({
      sortType: 'numeric',
      title: 'Created',
    }),
    headerClassName: tableStyles.sort,
    className: tableStyles.cell,
    width: 110,
    Cell({ original }): JSX.Element {
      return <Time format={TimeFormat.shortDateTime} timestamp={original.get('date_created')} />;
    },
  },
  {
    // The Table sorts server-side by column id. Mailrail partner messages send at date_start;
    // Pinpoint ones keep their send time in date_send_start, so they sort by creation time.
    id: 'date_start',
    Header: TableColumnHeader({
      sortType: 'numeric',
      title: 'Send Time',
    }),
    headerClassName: tableStyles.sort,
    className: tableStyles.cell,
    minWidth: 176,
    Cell({ original }): JSX.Element {
      return (
        <Time
          format={TimeFormat.shortDateTime}
          timestamp={getPartnerMessageSendTime(original as TPartnerMessage)}
        />
      );
    },
  },
  {
    id: 'from_address',
    Header: TableColumnHeader({
      sortType: 'alpha',
      title: 'From',
    }),
    headerClassName: tableStyles.sort,
    className: tableStyles.cell,
    minWidth: 170,
    accessor: 'attributes.from_address',
  },
  {
    id: 'sent',
    Header: TableColumnHeader({
      title: 'Sent',
    }),
    className: tableStyles.cellRight,
    maxWidth: 100,
    sortable: false,
    Cell(row): JSX.Element {
      const flavors = row.original.attributes.stats_cache?.flavors || {};
      const emailSend = flavors.email_send || 0;
      const partnerMessageSend = flavors.partner_message_send || 0;
      return <>{(emailSend + partnerMessageSend).toLocaleString()}</>;
    },
  },
  {
    id: 'opened',
    Header: TableColumnHeader({
      title: 'Opened',
    }),
    className: tableStyles.cellRight,
    maxWidth: 100,
    sortable: false,
    Cell(row): JSX.Element {
      const views = row.original.attributes.stats_cache?.num_views || 0;
      return <>{views.toLocaleString()}</>;
    },
  },
  {
    id: 'clicked',
    Header: TableColumnHeader({
      title: 'Clicked',
    }),
    className: tableStyles.cellRight,
    maxWidth: 100,
    sortable: false,
    Cell(row): JSX.Element {
      const clicks = row.original.attributes.stats_cache?.num_clicks || 0;
      return <>{clicks.toLocaleString()}</>;
    },
  },
  {
    id: 'options',
    Header: TableColumnHeader({
      title: 'Options',
    }),
    className: tableStyles.cellCenter,
    width: 80,
    sortable: false,
    Cell({ original }): JSX.Element {
      return <EventPartnersMessagesOptions message={original as TPartnerMessage} />;
    },
  },
];

export default eventPartnersMessagesColumns;
